import { cloneDeep, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as qs from 'qs';
import { PureComponent } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { isMobileApp } from '../../helpers/app';
import { CITY_ASSETS_PATH } from '../../helpers/constants';
import windowSize from '../../helpers/windowSize';
import injectReducer from '../../utils/injectReducer';
import injectSaga from '../../utils/injectSaga';
import { setFunnelStepAction } from '../App/actions';
import { META_DEFAULT_IMAGE, NAVIGATION, STEP_NONE } from '../App/constants';
import { makeSelectLocation } from '../App/selectors';
import routesJson from '../City/assets/json/routes.json';
import NotFound from '../NotFoundPage';
import {
  addEmptyEditableContentAction,
  deleteEditableContentAction,
  getCitiesAction,
} from './actions';
import {
  COMPANIES_DATA,
  CURRENT_PAGE_TYPE,
  DESTINATION_CITY,
  EDITABLE_CONTENT,
  LINKS,
  LOADING,
  ORIGIN_CITY,
  RATINGS,
  SHOULD_REDIRECT_TO_MAIN_PAGE,
  specificTableDataPropTypes,
  specificTableHeadersPropTypes,
  TRIP_DATA,
} from './constants';
import { FaqTextGen } from './helpers/FaqTextGen';
import { GeneralTextGen } from './helpers/GeneralTextGen';
import { SpecificTextGen } from './helpers/SpecificTextGen';
import { TextGen } from './helpers/TextGen';
import reducer from './reducer';
import saga from './saga';
import makeSelectSearch, {
  selectSpecificTableData,
  selectSpecificTableHeaders,
} from './selectors';
import { extractSearchPageDynamicInformation } from './utils';
import { SearchView } from './View';
import { RedirectToMainPage } from './View/RedirectToMainPage';

export class Search extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isTabletWidth: false,
    };
    this.data = null;
    this.intl = props.intl;
    this.origin = props.match.params.origin;
    this.destination = props.match.params.destination;
    this.textGen = new TextGen(this.props.location.pathname, props.intl);
    this.generalTextGen = new GeneralTextGen(this.textGen);
    this.specTextGen = new SpecificTextGen(this.textGen);
    this.faqTextGen = new FaqTextGen(this.textGen);
    this.routes = routesJson;

    const qsParse = qs.parse(props.location.search, {
      ignoreQueryPrefix: true,
    });

    if (qsParse.cstmbg) {
      this.searchHeaderBackground = decodeURIComponent(qsParse.cstmbg);
    }

    if (qsParse.widgetDep && typeof qsParse.widgetDep === 'string') {
      this.defaultDepartureDate = qsParse.widgetDep;
    }
    if (qsParse.widgetRet && typeof qsParse.widgetRet === 'string') {
      this.defaultReturnDate = qsParse.widgetRet;
    }

    if (Object.keys(this.routes).includes(`/city/${this.origin}`)) {
      import(`../City/assets/json/${this.routes[`/city/${this.origin}`]}`).then(
        (originAirtableData) => {
          this.originCityCard = cloneDeep(
            originAirtableData.destinationCards[0],
          );
          this.originCityCard.destinationCard.image = `${CITY_ASSETS_PATH}/${this.originCityCard.destinationCard.image}`;
        },
      );
    }

    if (Object.keys(this.routes).includes(`/city/${this.destination}`)) {
      import(
        `../City/assets/json/${this.routes[`/city/${this.destination}`]}`
      ).then((destinationAirtableData) => {
        this.destinationCityCard = cloneDeep(
          destinationAirtableData.destinationCards[0],
        );
        this.metaImage = destinationAirtableData.metaImage;
        this.destinationCityCard.destinationCard.image = `${CITY_ASSETS_PATH}/${this.destinationCityCard.destinationCard.image}`;
        this.searchHeaderBackground = `${CITY_ASSETS_PATH}/${destinationAirtableData.searchHeader.backgroundImage}`;
      });
    }

    this.props.setFunnelStep(STEP_NONE);
    this.props.getCities(
      this.origin,
      this.destination,
      this.props.match.params.pageType,
    );
  }

  componentDidMount() {
    this.handleSmallSizeWindowResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.detectTabletWidthSearchPage);
  }

  /**
   * @description Handle window resize for small devices
   */
  handleSmallSizeWindowResize = () => {
    if (windowSize.smallSize()) {
      this.setState({ isTabletWidth: true });
    }
    window.addEventListener('resize', this.detectTabletWidthSearchPage);
  };

  /**
   * @description Updates component state when size is corresponding to the right one
   */
  detectTabletWidthSearchPage = () => {
    const { isTabletWidth } = this.state;
    if (windowSize.smallSize()) {
      this.setState({ isTabletWidth: true });
    } else if (!windowSize.smallSize() && isTabletWidth) {
      this.setState({ isTabletWidth: false });
    }
  };

  render() {
    if (!this.props.values[LOADING] && isEmpty(this.props.values[TRIP_DATA])) {
      return <NotFound />;
    }

    if (this.props.values[SHOULD_REDIRECT_TO_MAIN_PAGE]) {
      return (
        <RedirectToMainPage
          link={`${window.location.protocol}//${window.location.host}${NAVIGATION.SEARCH}/${this.origin}/${this.destination}`}
        />
      );
    }

    const dynamicInformation = extractSearchPageDynamicInformation(
      this.props.values[TRIP_DATA],
      this.props.values[CURRENT_PAGE_TYPE],
    );

    return (
      <SearchView
        addEmptyEditableContent={this.props.addEmptyEditableContent}
        admin={this.props.admin}
        companiesData={this.props.values[COMPANIES_DATA]}
        currentPageType={this.props.values[CURRENT_PAGE_TYPE]}
        defaultDepartureDate={this.defaultDepartureDate}
        defaultReturnDate={this.defaultReturnDate}
        deleteEditableContent={this.props.deleteEditableContent}
        destinationCity={this.props.values[DESTINATION_CITY]}
        destinationCityCard={this.destinationCityCard}
        dynamicInformation={dynamicInformation}
        editableContent={this.props.values[EDITABLE_CONTENT]}
        generalTextData={this.generalTextGen.formatGeneralTextData(
          this.props.values,
          dynamicInformation,
        )}
        isReduced={isMobileApp(navigator.userAgent)}
        isTabletWidth={this.state.isTabletWidth}
        links={this.props.values[LINKS]}
        loading={this.props.values[LOADING]}
        metaImage={this.metaImage || META_DEFAULT_IMAGE}
        originCity={this.props.values[ORIGIN_CITY]}
        originCityCard={this.originCityCard}
        ratings={this.props.values[RATINGS]}
        searchHeaderBackground={this.searchHeaderBackground}
        shortFaqs={this.faqTextGen.formatShortFaqs(this.props.values)}
        specificTableData={this.props.specificTableData}
        specificTableHeaders={this.props.specificTableHeaders}
        specificTextData={this.specTextGen.formatSpecificTextData(
          this.props.values,
        )}
        tripData={this.props.values[TRIP_DATA]}
      />
    );
  }
}

Search.propTypes = {
  addEmptyEditableContent: PropTypes.func,
  admin: PropTypes.object,
  deleteEditableContent: PropTypes.func,
  getCities: PropTypes.func.isRequired,
  intl: PropTypes.object,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      destination: PropTypes.string,
      origin: PropTypes.string,
      pageType: PropTypes.string,
    }),
  }),
  setFunnelStep: PropTypes.func,
  specificTableData: specificTableDataPropTypes,
  specificTableHeaders: specificTableHeadersPropTypes,
  values: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  values: makeSelectSearch(),
  location: makeSelectLocation(),
  specificTableHeaders: selectSpecificTableHeaders(),
  specificTableData: selectSpecificTableData(),
});

export function mapDispatchToProps(dispatch) {
  return {
    getCities: (origin, destination, pageType) =>
      dispatch(getCitiesAction(origin, destination, pageType)),
    setFunnelStep: (step) => dispatch(setFunnelStepAction(step)),
    addEmptyEditableContent: () => dispatch(addEmptyEditableContentAction()),
    deleteEditableContent: (priority) =>
      dispatch(deleteEditableContentAction(priority)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withReducer = injectReducer({ key: 'search', reducer });
const withSaga = injectSaga({ key: 'search', saga });
export const SearchIntl = injectIntl(Search);

export default compose(withReducer, withSaga, withConnect)(SearchIntl);
